import { gql, useQuery } from "@apollo/client";

const GET_WORK_ORDER_FOR_TIME_TRACKING = gql`
  query getWorkOrderForTimeTracking($id: ID!) {
    getOwnOemTicketById(id: $id) {
      _id
      title
      status
      totalTimeLoggedInSeconds
      timeTrackerLogs {
        _id
        description
        startDateTime
        endDateTime
        isBillable
        timeElapsedInSeconds
        timezone
        source
        ticketTag {
          _id
          label
          color
        }
        createdBy {
          _id
          name
        }
        customFields {
          fieldId {
            _id
            label
            fieldType
          }
          value
          values
        }
      }
    }
  }
`;

export interface WorkOrderTimeTrackerTagRef {
  _id: string;
  label: string;
  color: string;
}

export interface WorkOrderTimeTrackerCreatedByRef {
  _id: string;
  name?: string;
}

export interface WorkOrderTimeTrackerCustomField {
  fieldId: {
    _id: string;
    label?: string;
    fieldType?: string;
  } | null;
  value?: unknown;
  values?: unknown[];
}

export interface WorkOrderTimeTrackerLog {
  _id: string;
  description?: string;
  startDateTime: string;
  endDateTime: string;
  isBillable: boolean;
  timeElapsedInSeconds: number;
  /** IANA timezone the entry was logged in; null for older entries. */
  timezone?: string | null;
  /** `"procedure"` when synced from a finalized procedure; such logs are read-only. */
  source?: string | null;
  ticketTag: WorkOrderTimeTrackerTagRef | null;
  createdBy: WorkOrderTimeTrackerCreatedByRef | null;
  customFields?: WorkOrderTimeTrackerCustomField[];
}

export interface WorkOrderForTimeTracking {
  _id: string;
  title: string;
  status: string;
  totalTimeLoggedInSeconds?: number;
  timeTrackerLogs: WorkOrderTimeTrackerLog[];
}

interface UseGetWorkOrderForTimeTrackingResult {
  workOrder: WorkOrderForTimeTracking | null;
  loading: boolean;
}

/**
 * Slim query for the TimeTracking tab. Pulls only the fields the tab renders
 * (time logs + status for closed-check + title for the entry form header).
 */
export const useGetWorkOrderForTimeTracking = (
  id: string | undefined,
): UseGetWorkOrderForTimeTrackingResult => {
  const { data, loading } = useQuery<{
    getOwnOemTicketById: WorkOrderForTimeTracking | null;
  }>(GET_WORK_ORDER_FOR_TIME_TRACKING, {
    variables: { id },
    skip: !id,
  });

  return {
    workOrder: data?.getOwnOemTicketById ?? null,
    loading,
  };
};
